import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';
import { KnowledgeApi } from 'app/domains/aya/apis/knowledge.api';
import { EnumKnowledgeDocumentStatus } from 'app/domains/aya/enums/knowledge-document-status.enum';
import { EnumKnowledgeIngestionStage } from 'app/domains/aya/enums/knowledge-ingestion-stage.enum';
import { IKnowledgeDocument } from 'app/domains/aya/interfaces/knowledge-document.interface';

const POLL_INTERVAL_MS = 2000;
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_DOCUMENTS = 100;
const MAX_TOTAL_BYTES = 500 * 1024 * 1024;

const ACCEPTED_EXTENSIONS = [
  'pdf',
  'txt',
  'md',
  'markdown',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff'
];

const STAGE_LABELS: { [stage: string]: string } = {
  [EnumKnowledgeIngestionStage.Validate]: 'Validando arquivo',
  [EnumKnowledgeIngestionStage.Extract]: 'Extraindo conteúdo',
  [EnumKnowledgeIngestionStage.Ocr]: 'Reconhecendo texto',
  [EnumKnowledgeIngestionStage.Normalize]: 'Organizando conteúdo',
  [EnumKnowledgeIngestionStage.Chunk]: 'Preparando trechos',
  [EnumKnowledgeIngestionStage.Embed]: 'Preparando busca',
  [EnumKnowledgeIngestionStage.Index]: 'Finalizando',
  [EnumKnowledgeIngestionStage.Cleanup]: 'Excluindo dados'
};

const STATUS_LABELS: { [status: string]: string } = {
  [EnumKnowledgeDocumentStatus.Queued]: 'Na fila',
  [EnumKnowledgeDocumentStatus.Processing]: 'Processando',
  [EnumKnowledgeDocumentStatus.Ready]: 'Pronto',
  [EnumKnowledgeDocumentStatus.Failed]: 'Falhou'
};

/* eslint-disable @typescript-eslint/naming-convention -- as chaves são os códigos do contrato */
const ERROR_MESSAGES: { [code: string]: string } = {
  file_too_large: 'O arquivo excede o tamanho máximo de 50 MB.',
  unsupported_media_type: 'Formato não aceito. Envie PDF, TXT, MD ou imagem.',
  knowledge_quota_exceeded: 'Você atingiu o limite da sua biblioteca.',
  rate_limit_exceeded: 'Muitas ações em pouco tempo. Tente novamente em instantes.',
  invalid_title: 'O título informado não é válido.',
  invalid_upload: 'O arquivo não pôde ser enviado. Verifique se ele não está vazio.',
  ingestion_in_progress: 'Este documento já está sendo processado.',
  document_not_found: 'Este documento não está mais disponível.',
  knowledge_unavailable: 'O serviço está indisponível no momento. Tente novamente em instantes.',
  forbidden: 'Você não tem acesso a este recurso.',
  tenant_not_found: 'Configuração pendente. Fale com o administrador.'
};
/* eslint-enable @typescript-eslint/naming-convention */

interface IKnowledgeCard {
  document: IKnowledgeDocument;
  isBusy: boolean;
}

@Component({
  selector: 'sgt-knowledge-section',
  templateUrl: './knowledge-section.component.html',
  styleUrls: ['./knowledge-section.component.scss']
})
export class KnowledgeSectionComponent implements OnInit, OnDestroy {
  cards: IKnowledgeCard[] = [];
  isLoading = false;
  isUploading = false;
  isLimitsOpen = false;

  readonly statuses = EnumKnowledgeDocumentStatus;
  readonly maxDocuments = MAX_DOCUMENTS;
  readonly acceptAttribute = ACCEPTED_EXTENSIONS.map(ext => `.${ext}`).join(',');

  private destroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();
  private isPolling = false;

  constructor(
    private knowledgeApi: KnowledgeApi,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get usedBytes(): number {
    return this.cards.reduce((total, card) => total + card.document.sizeBytes, 0);
  }

  get documentsUsageLabel(): string {
    return `${this.cards.length} de ${MAX_DOCUMENTS} arquivos`;
  }

  get storageUsageLabel(): string {
    return `${this.formatSize(this.usedBytes)} de 500 MB`;
  }

  trackByDocumentId(_: number, card: IKnowledgeCard): string {
    return card.document.id;
  }

  statusLabel(document: IKnowledgeDocument): string {
    return STATUS_LABELS[document.status] ?? '';
  }

  detailLabel(document: IKnowledgeDocument): string {
    if (document.status === EnumKnowledgeDocumentStatus.Failed) {
      return document.latestIngestion?.errorMessage ?? 'Não foi possível preparar este arquivo.';
    }
    if (!this.isPending(document)) {
      return '';
    }
    if (document.status === EnumKnowledgeDocumentStatus.Queued) {
      return STATUS_LABELS[EnumKnowledgeDocumentStatus.Queued];
    }
    return STAGE_LABELS[document.latestIngestion?.stage ?? ''] ?? '';
  }

  iconClass(document: IKnowledgeDocument): string {
    const mime = document.detectedMime;
    if (mime === 'application/pdf') {
      return 'far fa-file-pdf';
    }
    if (mime.startsWith('image/')) {
      return 'far fa-file-image';
    }
    return 'far fa-file-alt';
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${Math.round(kb)} KB`;
    }
    const mb = kb / 1024;
    return `${mb.toFixed(1).replace('.', ',')} MB`;
  }

  openLimits(): void {
    this.isLimitsOpen = true;
  }

  closeLimits(): void {
    this.isLimitsOpen = false;
  }

  toggleLimits(): void {
    this.isLimitsOpen = !this.isLimitsOpen;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    files.forEach(file => this.upload(file));
  }

  upload(file: File): void {
    const rejection = this.rejectionReason(file);
    if (rejection) {
      this.toastService.error(rejection);
      return;
    }

    this.isUploading = true;

    this.knowledgeApi
      .upload({ file, idempotencyKey: this.buildIdempotencyKey() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: document => {
          this.isUploading = false;
          this.cards = [this.buildCard(document), ...this.cards];
          this.syncPolling();
        },
        error: error => {
          this.isUploading = false;
          this.toastService.error(this.errorMessage(error, 'Não foi possível enviar o arquivo.'));
        }
      });
  }

  toggleEnabled(card: IKnowledgeCard): void {
    const previous = card.document.enabled;
    const next = !previous;

    card.document = { ...card.document, enabled: next };
    card.isBusy = true;

    this.knowledgeApi
      .update(card.document.id, { enabled: next })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: document => {
          card.isBusy = false;
          card.document = document;
        },
        error: error => {
          card.isBusy = false;
          card.document = { ...card.document, enabled: previous };
          this.toastService.error(
            this.errorMessage(error, 'Não foi possível alterar o uso deste documento.')
          );
        }
      });
  }

  reprocess(card: IKnowledgeCard): void {
    card.isBusy = true;

    this.knowledgeApi
      .reprocess(card.document.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: document => {
          card.isBusy = false;
          card.document = document;
          this.syncPolling();
        },
        error: error => {
          card.isBusy = false;
          this.toastService.error(
            this.errorMessage(error, 'Não foi possível reprocessar este documento.')
          );
        }
      });
  }

  download(card: IKnowledgeCard): void {
    card.isBusy = true;

    this.knowledgeApi
      .downloadSource(card.document.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: blob => {
          card.isBusy = false;
          this.triggerDownload(blob, card.document.originalFilename);
        },
        error: error => {
          card.isBusy = false;
          this.toastService.error(this.errorMessage(error, 'Não foi possível baixar o arquivo.'));
        }
      });
  }

  confirmDelete(card: IKnowledgeCard): void {
    card.isBusy = true;

    this.knowledgeApi
      .delete(card.document.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.cards = this.cards.filter(item => item !== card);
          this.syncPolling();
        },
        error: error => {
          card.isBusy = false;
          this.toastService.error(
            this.errorMessage(error, 'Não foi possível excluir este documento.')
          );
        }
      });
  }

  isPending(document: IKnowledgeDocument): boolean {
    return (
      document.status === EnumKnowledgeDocumentStatus.Queued ||
      document.status === EnumKnowledgeDocumentStatus.Processing
    );
  }

  private load(): void {
    this.isLoading = true;

    this.knowledgeApi
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: page => {
          this.isLoading = false;
          this.cards = page.items.map(document => this.buildCard(document));
          this.syncPolling();
        },
        error: error => {
          this.isLoading = false;
          this.cards = [];
          this.toastService.error(
            this.errorMessage(error, 'Não foi possível carregar seus documentos.')
          );
        }
      });
  }

  private syncPolling(): void {
    const hasPending = this.cards.some(card => this.isPending(card.document));

    if (!hasPending) {
      this.stopPolling$.next();
      this.isPolling = false;
      return;
    }

    if (this.isPolling) {
      return;
    }

    this.isPolling = true;

    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.knowledgeApi.list()),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: page => {
          this.reconcile(page.items);
          this.syncPolling();
        },
        error: () => {
          this.isPolling = false;
          this.stopPolling$.next();
        }
      });
  }

  private reconcile(documents: IKnowledgeDocument[]): void {
    this.cards = documents.map(document => {
      const existing = this.cards.find(card => card.document.id === document.id);
      return { document, isBusy: existing?.isBusy ?? false };
    });
  }

  private buildCard(document: IKnowledgeDocument): IKnowledgeCard {
    return { document, isBusy: false };
  }

  private rejectionReason(file: File): string | null {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      return `"${file.name}" não é um formato aceito. Envie PDF, TXT, MD ou imagem.`;
    }
    if (file.size > MAX_FILE_BYTES) {
      return `"${file.name}" excede o tamanho máximo de 50 MB.`;
    }
    if (this.cards.length >= MAX_DOCUMENTS) {
      return `Você atingiu o limite de ${MAX_DOCUMENTS} arquivos.`;
    }
    if (this.usedBytes + file.size > MAX_TOTAL_BYTES) {
      return 'Espaço insuficiente na sua biblioteca. Exclua um arquivo para liberar espaço.';
    }
    return null;
  }

  private buildIdempotencyKey(): string {
    const cryptoRef = window.crypto as Crypto & { randomUUID?: () => string };
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  private errorMessage(
    error: { error?: { error?: { code?: string; message?: string } } },
    fallback: string
  ): string {
    const code = error?.error?.error?.code;
    return (code && ERROR_MESSAGES[code]) || fallback;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
